import { styled, Tooltip, Typography } from '@mui/material';
import AppointmentApi, { AppointmentInterface } from 'api/counter/appointment.api';
import ClinicApi, { DentistInterface } from 'api/master/clinic.api';
import AutocompleteSelect from 'component/Select/AutocompleteSelect';
import TableCustom, { TablePaginationMiniCustom } from 'component/Table/TableCustom';
import TableRowCommon from 'component/Table/TableRowCommon';
import { CustomFilterDoctor, ListFilterDoctors } from 'features/counter/appointment/head-appointment';
import _, { floor } from 'lodash';
import moment from 'moment';
import { useCallback, useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { dateToView } from 'utils/date.utils';
import UseWindowSize from 'utils/useWindowSize';

import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import InputSecondNewRangePicker, { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker';
import { notiError } from 'component/notifications/notifications';
import { colors } from 'constants/theme';
import MainButton from 'new-components/buttons/main-button';
import NewIconButton from 'new-components/buttons/new-icon-button';
import SelectInput from 'new-components/inputs/select-input';
import PageModal from 'new-components/modal/page-modal';
import { getBranch } from 'utils/app.utils';
export interface IViewHistoryProps {
  onClose: () => void
  onPrint: (e: any) => void
  patientId: string
}

/** STYLE CUSTOM */
const TableConfig = styled('div')(({ theme }) => ({
  td: { height: '48px' },
  '.MuiTableBody-root': {
    '.MuiTableCell-root': {
      verticalAlign: 'top !important',
      paddingBottom: '12px !important'
    }
  },
  '& .appointmentOperatives': { maxWidth: '200px' },
  '& .practice': { minWidth: '300px', maxWidth: '500px' },
  '& .remark': { minWidth: '300px', maxWidth: '500px' },
  '.MuiTableContainer-root': {
    maxHeight: 'calc(56px + 10 * 52px) !important',
    overflowY: 'auto !important',
  },
  '.MuiPaper-root table thead': {
    backgroundColor: `${colors.themeMainColor} !important`,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  '.MuiPaper-root table thead tr th': {
    position: 'sticky',
    top: 0,
    zIndex: 1,
  }
}))

const THead = styled('div')(({ theme }) => ({
  display: 'flex',
  backgroundColor: colors.themeMainColor,
  borderRadius: '8px 8px 0 0',
  padding: '0.5rem 1.5rem',
  fontSize: 16,
  color: colors.white
}))

const TBody = styled('div')(({ theme }) => ({
  border: `1px solid ${colors.lightGray}`,
  borderTopWidth: 0,
  color: colors.textPrimary,
  fontSize: 16,
  padding: '0.625rem 1.5rem',
  '.expand-more': {
    backgroundColor: colors.white,
    border: `1px solid ${colors.textLightGray}`,
    color: colors.themeMainColor,
    borderRadius: 100,
    fontSize: 22
  }
}))

const Cell = styled('div')(({ theme }) => ({ '&:first-of-type': { marginRight: '1.25rem', width: '100%' } }))
const initialDateRange: [string, string] = [moment().format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')];

export default function ViewAppointment(props: IViewHistoryProps) {
  const { t } = useTranslation()

  const [doctors, setDoctors] = useState<DentistInterface[]>([])
  const [appointments, setAppointments] = useState<AppointmentInterface[]>([])
  const [width] = UseWindowSize()
  /** PAGINATION  */
  const [page, setPage] = useState(1)
  const [pageLimit, setPageLimit] = useState(width > 1200 ? 3 : 10)
  const [rowCount, setRowCount] = useState(0)
  const [sortBy, setSortBy] = useState('date')
  const [sortType, setSortType] = useState('DESC')
  const [status, setStatus] = useState<boolean>(true)

  /** FILTER */
  const [search, setSearch] = useState('')
  const [doctor, setDoctor] = useState<number>(0)
  const [rangeDate, setRangeDate] = useState<[string, string]>(initialDateRange)

  const [fetch, setFetch] = useState<boolean>(false);
  const [conditionLoad, setConditionLoad] = useState<any>({})

  const [branchId, setBranchId] = useState<any>(getBranch())
  const [branches, setBranches] = useState<any[]>([])
  const [appliedFilters, setAppliedFilters] = useState({
    search: '',
    branchId: getBranch(),
    rangeDate: initialDateRange,
    doctor: 0
  })

  const loadDataAppointment = async () => {
    try {
      let condition: any = {}
      if (page) condition = { ...condition, page: page }
      if (pageLimit) condition = { ...condition, pageLimit: pageLimit }
      if (sortBy) condition = { ...condition, sortBy: sortBy }
      if (sortType) condition = { ...condition, sortType: sortType }
      if (appliedFilters.branchId !== 'ALL') condition = { ...condition, branchId: appliedFilters.branchId }
      if (appliedFilters.doctor) condition = { ...condition, doctorId: appliedFilters.doctor }
      if (props.patientId) condition = { ...condition, patientId: props.patientId }
      if (appliedFilters.rangeDate && appliedFilters.rangeDate[0] && appliedFilters.rangeDate[1]) {
        condition = { ...condition, dateStart: appliedFilters.rangeDate[0], dateEnd: appliedFilters.rangeDate[1] }
      }
      await AppointmentApi.findAll(condition).then(({ data, headers }) => {
        if (data) {
          const newData: any = data.sort((a: any, b: any) => {
            return new Date(b.date).getTime() - new Date(a.date).getTime();
          });
          setRowCount(headers['x-total'])
          setAppointments(newData)
          if (fetch || status) {
            setConditionLoad(condition)
          }
          setStatus(false)
        }
      }).finally(() => {
        setFetch(false)
      })
    } catch (error) {
      notiError('ERROR')
    }
  }


  useEffect(() => {
    loadDataAppointment()
  }, [page, pageLimit, sortBy, sortType, appliedFilters])

  const searchCilck = (() => {
    setPage(1)
    setAppliedFilters({ search: search, branchId, rangeDate: rangeDate, doctor })
  })

  const loadData = useCallback(() => { ClinicApi.findAllDentists().then(({ data }) => setDoctors(data)) }, [])

  const branchData = useCallback(() => { ClinicApi.findAllBranches().then(({ data }) => setBranches(_.map(data, (item) => ({ value: item.branchId, name: item.branchName || item.branchNameEn })))) }, [])
  useEffect(() => {
    loadData()
    branchData()
  }, [])


  const headCells = [
    { id: 'no', disablePadding: false, label: t('TABLE.NO'), class: 'text-nowrap' },
    { id: 'branchId', disablePadding: false, label: t('APPOINTMENT.HISTORY.BRANCH'), class: 'text-nowrap' },
    { id: 'date', disablePadding: false, label: t('APPOINTMENT.HISTORY.APPOINTMENT_DATE'), class: 'text-nowrap' },
    { id: 'timeStart', disablePadding: false, label: t('APPOINTMENT.HISTORY.TIME_START'), class: 'text-nowrap' },
    { id: 'durationMinute', disablePadding: false, label: t('APPOINTMENT.HISTORY.APPOINTMENT_DURATION'), class: 'text-nowrap' },
    { id: 'doctorFullname', disablePadding: false, label: t('APPOINTMENT.HISTORY.DOCTOR'), class: 'text-nowrap', width: '12rem' },
    { id: 'appointmentOperatives', disablePadding: false, label: t('APPOINTMENT.HISTORY.SERVICE'), class: 'text-nowrap', width: '30rem' },
    { id: 'remark', disablePadding: false, label: t('APPOINTMENT.HISTORY.REMARK'), class: 'text-nowrap', width: '15rem' },
    { id: 'action', disablePadding: false, label: t('APPOINTMENT.HISTORY.ACTION'), width: '6rem' },
  ]

  const getDurationTextByTimeMinute = (minute: number) => {
    if (minute >= 60) return `${floor(minute / 60)} ${t('TIME_DURATION_TYPE.HOURS')} ` + (minute % 60 > 0 ? `${minute % 60} ${t('TIME_DURATION_TYPE.MINUTES')}` : '')
    else return `${minute} ${t('TIME_DURATION_TYPE.MINUTES')}`
  }

  const renderDataSlim = (objData: AppointmentInterface, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { appointmentId, branchName, date, timeStart, durationMinute, cnNumber, branchCnNumber, patientFullname, doctorFullname, appointmentOperatives, remark } = objData
    return (
      <TBody>
        <div className={'d-flex align-items-center'}>
          <Cell>
            <Row className="pl-4 mx-0">
              <div className="position-absolute w-auto px-0" style={{ marginLeft: '-1.5rem' }}>{no}</div>
              <Col xs={6} sm={3} className="font-weight-bold">{t('APPOINTMENT.HISTORY.BRANCH')}</Col>
              <Col xs={6} sm={3}>{branchName}</Col>
              <Col xs={6} sm={3} className="font-weight-bold">{t('APPOINTMENT.HISTORY.APPOINTMENT_DATE')}</Col>
              <Col xs={6} sm={3}>{dateToView(date)}</Col>
              <Col xs={6} sm={3} className="font-weight-bold">{t('APPOINTMENT.HISTORY.TIME_START')}</Col>
              <Col xs={6} sm={3}>{`${timeStart.replace(':00', '').replace(':', '.')} ${t('TIME_UNIT')}`}</Col>
              <Col xs={6} sm={3} className="font-weight-bold">{t('APPOINTMENT.HISTORY.DOCTOR')}</Col>
              <Col xs={6} sm={3}>{doctorFullname || '-'}</Col>
              <Col xs={6} sm={3} className="font-weight-bold">{t('APPOINTMENT.HISTORY.SERVICE')}</Col>
              <Col xs={6} sm={3}>
                <Tooltip title={String(_.map(appointmentOperatives, (op: any) => op.operativeName || op.operativeTypeName).join(', '))} placement="left">
                  <div>{String(_.map(appointmentOperatives, (op: any) => op.operativeName || op.operativeTypeName).join(', '))}</div>
                </Tooltip>
              </Col>
              <Col xs={6} sm={3} className="font-weight-bold">{t('APPOINTMENT.HISTORY.REMARK')}</Col>
              <Col xs={6} sm={3} className={'hard-break'}>{remark || '-'}</Col>
              <Col xs={6} sm={3} className="font-weight-bold">{t('APPOINTMENT.HISTORY.ACTION')}</Col>
              <Col xs={6} sm={3} className="-font-weight-bold">
                <NewIconButton icon={<PrintOutlinedIcon />} onClick={() => props.onPrint(appointmentId)} size='medium' type='primary' variant='outlined' />
              </Col>
            </Row>
          </Cell>
        </div>
      </TBody>
    )
  }


  const renderData = (objData: AppointmentInterface, no: number) => {
    no = page * pageLimit - pageLimit + no + 1
    const { appointmentId, branchName, date, timeStart, durationMinute,
      doctorFullname, appointmentOperatives,
      remark } = objData
    const objRenderData = {
      key: String(appointmentId),
      id: String(appointmentId),
      obj: objData,
      columns: [
        { option: 'TEXT', align: 'center', label: no },
        { option: 'TEXT', align: 'left', label: branchName || '', class: 'text-nowrap-ellipsis' },
        { option: 'TEXT', align: 'left', label: dateToView(date), class: 'text-nowrap-ellipsis' },
        { option: 'TEXT', align: 'center', label: `${timeStart.replace(':00', '').replace(':', '.')} น.` },
        { option: 'TEXT', align: 'center', label: getDurationTextByTimeMinute(durationMinute) },
        { option: 'TEXT', align: 'left', label: doctorFullname, class: 'text-nowrap-ellipsis' },
        {
          option: 'COMPONENT', align: 'left', component: (<div>
            <Tooltip title={String(_.map(_.filter(appointmentOperatives, (op: any) => op.operativeName || op.operativeTypeName), (op: any) => op.operativeName || op.operativeTypeName).join(', '))} placement='bottom'>
              <div>{String(_.map(_.filter(appointmentOperatives, (op: any) => op.operativeName || op.operativeTypeName), (op: any) => op.operativeName || op.operativeTypeName).join(', '))}</div>
            </Tooltip>
          </div>)
        },
        { option: 'TEXT', align: 'left', label: remark },
        {
          option: 'COMPONENT', align: 'center', component:
            <div style={{ display: 'flex', justifyContent: 'center' }}><NewIconButton icon={<PrintOutlinedIcon />} onClick={() => props.onPrint(appointmentId)} size='medium' type='primary' variant='outlined' /></div>
        }
      ]
    }
    return <TableRowCommon {...objRenderData} />
  }

  return <PageModal
    isOpen
    size={'L'}
    title={t('BRANCH.INPUT.OPD_APPOINTMENT')}
    handleClose={props.onClose}
    onCloseSubmit
    onSubmit={{ onClick: () => null }}
    onCancel={{ onClick: props.onClose }}
    overflow
    sx={{ zIndex: 2000 }}
  >
    <>
      <Row className="mb-2 mx-0 mt-2">
        <Col xs={12} sm={6} md={4} xl={2}>
          <SelectInput
            onChange={(e) => {
              setBranchId(e.target.value)
              setPage(1)
            }}
            options={[{ value: 'ALL', name: t('STATUS.ALL') }, ...branches]}
            label={t('REPORT.FILTER.BRANCH')}
            value={branchId}
          />
        </Col>
        <Col xs={12} sm={6} md={4} xl={2} className="px-1 mb-2">
          <CustomFilterDoctor className='position-relative' >
            <Typography className='position-absolute edit-filter-doctor'>{t('USER_DOCTOR')}:</Typography>
            <AutocompleteSelect
              labelId="select-dentist"
              options={doctors}
              getOptionLabel={(option) => option.dentistFullname || t('ALL')}
              renderOption={(props, option) => {
                return (
                  <ListFilterDoctors {...props} key={option.dentistId} component="li">
                    {option.dentistFullname}
                  </ListFilterDoctors>
                )
              }}
              onchange={(e, value) => setDoctor(value?.dentistId)}
              noOptionsText={t('NOT_FOUND')}
              filterOptions={(option) => option.dentistFullname}
              value={_.find(doctors, { dentistId: doctor }) || ''}
              height={40}
              disableClearable={!doctor}
            />
          </CustomFilterDoctor>
        </Col>
        <Col xs={12} sm={6} md={4} xl={2} className="px-1 mb-2">
          <InputSecondNewRangePicker
            value={rangeDate}
            inputHeight={40}
            onchange={(val: any) => {
              setRangeDate([moment(val[0]).format('YYYY-MM-DD'), moment(val[1]).format('YYYY-MM-DD')] as [string, string])
              setPage(1)
            }}
            allowClear
            onClear={() => {
              const cleared = emptyRangePicker as [string, string]
              setRangeDate(cleared)
              setPage(1)
              setAppliedFilters((prev) => ({ ...prev, rangeDate: cleared }))
            }}
          />
        </Col>
        <Col xs={12} md={6} xl={1} className="px-1 mb-2">
          <MainButton
            onClick={() => searchCilck()}
            title={t('INPUT.SEARCH')}
            type='primary'
            variant='contained'
            style={{ width: '120px' }}
          />
        </Col>
      </Row>
      <div className="d-none d-xl-block">
        <TableConfig>
          <TableCustom
            page={page}
            pageLimit={pageLimit}
            sortType={sortType}
            sortBy={sortBy}
            rowCount={rowCount}
            onSort={(sortByVal: string, sortTypeVal: string) => {
              setSortType(sortTypeVal)
              setSortBy(sortByVal)
            }}
            setPageLimit={setPageLimit}
            setPage={setPage}
            headCells={headCells}
            rowsData={_.map(appointments, (val, i) => renderData(val, i))}
          />
        </TableConfig>
      </div>
      <div className="d-block d-xl-none">
        <THead>
          <Cell>{t('#')}</Cell>
        </THead>
        <div className={'treatment-body'} >
          <div style={{ maxHeight: 'calc(100vh - 500px)', overflowY: 'auto' }}>{_.map(appointments, renderDataSlim)}</div>
          <div className={'mt-2'}>
            <TablePaginationMiniCustom
              count={rowCount || 0}
              page={page}
              rowsPerPage={pageLimit}
              rowCount={rowCount}
              pageLimit={pageLimit}
              headCells={headCells}
              onPageChange={(event: React.MouseEvent<HTMLButtonElement> | null, newPage: number) => setPage(newPage)}
              setPageLimit={(event: any) => {
                setPageLimit(parseInt(event.target.value, 10))
                setPage(1)
              }}
              setPage={setPage}
            />
          </div>
        </div>
      </div>
    </>
  </PageModal >
}
